import PropTypes from 'prop-types';
import React, { useRef, useCallback, memo, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { fromJS, List, Map } from 'immutable';
import classNames from 'classnames';
import { ContextMenu, MenuItem, ContextMenuTrigger, showMenu } from 'react-contextmenu';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import ChangeDetection from '../../decorators/ChangeDetection';
import { unwrap } from '../../../utils/ChangeSpy';
import uniqueId from '../../../utils/uniqueId';
import arrayMove from '../../../utils/arrayMove';
import copyToClipboard from '../../../utils/copyToClipboard';
import getContactMailLink from '../../../utils/getContactMailLink';
import { configureExecEmailMergedFields } from '../../../utils/configureEmailMergedFields';
import BaseInput from '../../helpers/Input';
import BaseSpan from '../../helpers/Span';
import { IconButton } from '../../helpers/IconButton';
import { EmailTemplatesContextMenuWrapper } from '../../helpers/EmailTemplatesContextMenuWrapper';
import DisplayError from '../../decorators/DisplayError';
import PrettyErrorDecorator from '../../decorators/PrettyError';
import { fetchExecutivesEmailTemplates } from '../../../actions/emailTemplates';
import { findDirectors } from '../../../actions/contact/contactExecutive';
import { LocalTimeZone } from '../../LocalTimeZone';

import style from './style.module.css';

const emptyList = List();

const Input = PrettyErrorDecorator(DisplayError(BaseInput));
const Span = PrettyErrorDecorator(DisplayError(BaseSpan));

const ContextMenuWrapper = memo(props => {
  const {
    component,
    contactId,
    contactValue,
    executiveInfo,
    emailTemplates,
    fetchEmailTemplates,
    findDirectors,
    isEmail,
    onClick,
    onOpenEmail,
    value,
  } = props;
  const templatesMenuRef = useRef(null);

  const id = uniqueId();
  const templatesMenuId = uniqueId();

  const handleSendEmailItemClick = event => {
    event.stopPropagation();

    const coords = templatesMenuRef.current.getBoundingClientRect();

    showMenu({
      position: { x: coords.right - 50, y: coords.bottom },
      id: templatesMenuId,
    });
  };

  const menuItems = contactId !== 'new' && (
    <>
      {isEmail && (
        <MenuItem onClick={handleSendEmailItemClick}>
          <div>
            <i className="fa fa-envelope" />
            Send an email
          </div>
        </MenuItem>
      )}
      <MenuItem onClick={() => {}}>
        <div onClick={() => copyToClipboard(value)}>
          <strong>
            <i className="fa fa-clipboard" />
          </strong>
          Copy
        </div>
      </MenuItem>
    </>
  );

  return (
    <div className={style.cellValue}>
      {isEmail && (
        <EmailTemplatesContextMenuWrapper
          companyInfo={executiveInfo}
          email={contactValue}
          emailTemplates={emailTemplates}
          fetchEmailTemplates={fetchEmailTemplates}
          findDirectors={findDirectors}
          id={templatesMenuId}
          onOpenEmail={onOpenEmail}
          templateType="executive"
        >
          <div ref={templatesMenuRef} />
        </EmailTemplatesContextMenuWrapper>
      )}
      <ContextMenuTrigger id={id}>{component}</ContextMenuTrigger>

      <ContextMenu id={id}>
        <MenuItem data={{ type: 'delete' }} onClick={onClick}>
          <div>
            <strong>
              <i className="fa fa-remove" />
            </strong>
            Delete
          </div>
        </MenuItem>
        {menuItems}
      </ContextMenu>
    </div>
  );
});

const LinkValue = ChangeDetection(({ ...props }) => <a {...props} />);

const DragHandle = SortableHandle(({ isInherited }) => (
  <td className={classNames(style.dragHandle, { 'cursor-move': !isInherited })}>
    {!isInherited && <i aria-hidden="true" className="fa fa-bars" />}
  </td>
));

const ContactValue = memo(props => {
  const {
    companyId,
    contactId,
    contactValue,
    companyName,
    onChange,
    onClick,
    onOpenEmail,
    isEditing,
    isInherited,
    isEmail,
    executiveInfo,
    emailTemplates,
    fetchEmailTemplates,
    findDirectors,
    name,
    autoFocus,
    phoneType,
    ...rest
  } = props;

  const { value = '' } = rest;

  if (isInherited) {
    return (
      <LinkValue checkOn="children" href={`/company/${companyId}`} target="_blank" title={companyName}>
        {value}
      </LinkValue>
    );
  }

  let result = (
    <Span checkOn="children" data-clipboard-action="copy" name={name} minSizes>
      {value}
    </Span>
  );

  if (isEditing) {
    result = (
      <Input
        autoFocus={autoFocus}
        className="form-control input-sm"
        name={name}
        onChange={onChange}
        phoneType={phoneType}
        placeholder=""
        value={value}
      />
    );
  }

  return (
    <ContextMenuWrapper
      component={result}
      contactId={contactId}
      contactValue={unwrap(contactValue)}
      emailTemplates={emailTemplates}
      executiveInfo={executiveInfo}
      fetchEmailTemplates={fetchEmailTemplates}
      findDirectors={findDirectors}
      isEmail={isEmail}
      onClick={onClick}
      onOpenEmail={onOpenEmail}
      value={unwrap(value)}
    />
  );
});

/**
 * Stateless component for contact table row.
 *
 * @param props {Object}.
 * @param props.contact {Immutable.Map} Contact's info.
 * Need *value* key at this moment.
 * @param props.isDraggable {boolean} When true renders the DragHandle cell.
 */
const ContactRow = memo(props => {
  const {
    isDraggable,
    phoneType,
    name,
    isEmail,
    isAddresses,
    contact,
    onChange,
    onDelete,
    onOpenEmail,
    executiveInfo,
    emailTemplates,
    fetchEmailTemplates,
    findDirectors,
    ...rest
  } = props;
  const isEditing = contact.has('isEditing');
  const isInherited = contact.get('isInherited');
  const companyId = contact.get('companyId');
  const companyName = contact.get('companyName');
  const timezone = contact.get('timezone');

  const value = contact.get('value');
  const isVerify = contact.get('isVerify');
  const type = contact.get('type');
  const contactId = contact.get('id');
  let verifiedColumn = null;
  let localTime = null;

  if (isEmail) {
    verifiedColumn = (
      <td>
        <i className={classNames('fa', { 'text-success fa-check': isVerify, 'text-danger fa-times': !isVerify })} />
      </td>
    );
  }

  if (isAddresses) {
    localTime = (
      <td>
        <LocalTimeZone format="h:mm A" timezone={timezone} showTimezoneAbbreviation />
      </td>
    );
  }

  /**
   * Open mailto link.
   *
   * @param {string} email Recipient email.
   */
  const handleOpenSingleEmail = email => {
    window.open(`mailto:${email}`, '_blank');
  };

  return (
    <tr {...rest} onDoubleClick={() => handleOpenSingleEmail(unwrap(value))}>
      {isDraggable && <DragHandle isInherited={isInherited} />}
      <td className={classNames({ clickable: !isEditing })}>
        <ContactValue
          companyId={companyId}
          companyName={companyName}
          contactId={contactId}
          contactValue={value}
          emailTemplates={emailTemplates}
          executiveInfo={executiveInfo}
          fetchEmailTemplates={fetchEmailTemplates}
          findDirectors={findDirectors}
          isEditing={isEditing}
          isEmail={isEmail}
          isInherited={isInherited}
          name={`${name}.value`}
          onChange={onChange}
          onClick={onDelete}
          onOpenEmail={onOpenEmail}
          phoneType={phoneType}
          value={value}
          autoFocus
        />
      </td>
      {verifiedColumn}
      <td className={classNames({ clickable: !isEditing })}>
        <ContactValue
          companyId={companyId}
          companyName={companyName}
          contactId={contactId}
          contactValue={value}
          emailTemplates={emailTemplates}
          executiveInfo={executiveInfo}
          fetchEmailTemplates={fetchEmailTemplates}
          findDirectors={findDirectors}
          isEditing={isEditing}
          isEmail={isEmail}
          isInherited={isInherited}
          name={`${name}.type`}
          onChange={onChange}
          onClick={onDelete}
          onOpenEmail={onOpenEmail}
          phoneType={phoneType}
          value={type}
        />
      </td>
      {localTime}
    </tr>
  );
});

ContactRow.propTypes = {
  contact: PropTypes.instanceOf(Map).isRequired,
  isDraggable: PropTypes.bool,
};

ContactRow.defaultProps = {
  isDraggable: false,
};

// Created at module level to avoid re-mounting the HOC on every render.
const SortableContactRow = SortableElement(ContactRow);
const SortableBody = SortableContainer(({ children }) => <tbody>{children}</tbody>);

const prepareItems = items => items.map((item, index) => ({ ...item.toJS(), priority: index }));

/**
 * Stateless component for contacts values of 1 type (phones/emails/addresses).
 *
 * @link http://screencloud.net/v/l6sv component usage example
 * @param props {Object}.
 * @param props.type {String} Contact's type name. Placed on top of table.
 * @param props.onAdd {Function} Callback. Called on click `plus` button.
 * @param props.contacts {Immutable.List} List of contact values' Immutable.Map.
 * @param props.onSort {Function} Optional. Called with (data) after drag ends.
 *   When omitted sorting is disabled and no drag handle is shown.
 */
const ContactTypedInfoTable = props => {
  const {
    type,
    onAdd,
    onClick,
    onDelete,
    onChange,
    contacts,
    executiveContact,
    executiveInfo,
    emailTemplates,
    fetchEmailTemplates,
    findDirectors,
    user,
    onSort,
  } = props;
  const [items, setItems] = useState(() => prepareItems(contacts || []));

  const isEmail = type === 'Emails';
  const isAddresses = type === 'Addresses';
  const isSortable = Boolean(onSort);
  // +1 column for the drag handle when sorting is active.
  const columnCount = useMemo(() => {
    if (isAddresses) return 3;

    return (isEmail ? 3 : 2) + (isSortable ? 1 : 0);
  }, [isEmail, isSortable, isAddresses]);

  let rows = (
    <tr>
      <td colSpan={columnCount}> No rows to display.</td>
    </tr>
  );

  useEffect(() => {
    setItems(prepareItems(contacts || []));
  }, [contacts]);

  /**
   * Get an object with email's merged fields.
   *
   * @return {object} Merged fields.
   */
  const getEmailMergedFields = useCallback(() => configureExecEmailMergedFields(executiveContact, user), [
    executiveContact,
    user,
  ]);

  /**
   * Parse email fields and open a mailto link.
   *
   * @param {string} email Recipient email.
   * @param {Map} template Email template.
   */
  const handleOpenEmail = useCallback(
    (email, template) => {
      const mergedFields = getEmailMergedFields();
      const mailLink = getContactMailLink(email, template, mergedFields, user, executiveContact);

      window.open(mailLink, '_blank');
    },
    [getEmailMergedFields, executiveContact, user],
  );

  const handleSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      setItems(prevItems => {
        const newItems = arrayMove(prevItems, oldIndex, newIndex);

        if (onSort) {
          onSort(newItems, oldIndex, newIndex);
        }

        return newItems;
      });
    },
    [onSort],
  );

  rows = items.map((contact, i) => {
    const { isInherited } = contact;
    const rowProps = {
      key: i,
      index: i,
      isDraggable: isSortable,
      contact: fromJS(contact),
      emailTemplates,
      executiveInfo,
      fetchEmailTemplates,
      findDirectors,
      isEmail,
      isAddresses,
      isInherited,
      name: `${type.toLowerCase()}.${i}`,
      onChange,
      onClick: event => onClick(event, i),
      onDelete: (event, data) => onDelete(event, i, data),
      onOpenEmail: handleOpenEmail,
      phoneType: type === 'Phones',
    };

    const shouldRenderSortable = isSortable && !isInherited;

    return shouldRenderSortable ? <SortableContactRow {...rowProps} /> : <ContactRow {...rowProps} />;
  });

  const tbody = useMemo(
    () =>
      isSortable ? (
        <SortableBody lockAxis="y" onSortEnd={handleSortEnd} useDragHandle>
          {rows}
        </SortableBody>
      ) : (
        <tbody>{rows}</tbody>
      ),
    [isSortable, rows, handleSortEnd],
  );

  return (
    <table className={classNames(style.host, 'table table-bordered table-hover')}>
      <thead>
        <tr>
          {isSortable && <th className={style.dragHandle} />}
          <th className={style.title}>
            <IconButton
              className={style.addButton}
              icon="plus"
              onClick={onAdd}
              size="sm"
              title={`Add ${type}`}
              variant="primary"
            />
            <span>{type}</span>
          </th>
          <th />
          {isEmail && <th />}
          {isAddresses && <th>Local Time</th>}
        </tr>
      </thead>
      {tbody}
    </table>
  );
};

ContactTypedInfoTable.propTypes = {
  contacts: PropTypes.instanceOf(List).isRequired,
  emailTemplates: PropTypes.instanceOf(List).isRequired,
  executiveContact: PropTypes.instanceOf(Map).isRequired,
  executiveInfo: PropTypes.instanceOf(Map).isRequired,
  fetchEmailTemplates: PropTypes.func.isRequired,
  findDirectors: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSort: PropTypes.func,
  type: PropTypes.string.isRequired,
  user: PropTypes.instanceOf(Map).isRequired,
};

const mapStateToProps = state => ({
  executiveInfo: state.contact.executive,
  executiveContact: state.contact.info,
  user: state.auth.get('user'),
  emailTemplates: state.emailTemplates.get('executivesData', emptyList),
});

const mapActionToProps = {
  fetchEmailTemplates: fetchExecutivesEmailTemplates,
  findDirectors,
};

export default connect(mapStateToProps, mapActionToProps)(memo(ContactTypedInfoTable));
